import MapboxDraw from '@mapbox/mapbox-gl-draw';

import GeoJSON from 'geojson';
import { throttle } from 'lodash';
import mapboxgl, { Marker } from 'mapbox-gl';

import { getConfig } from '../../utils/getConfig.ts';
import { DRAW_MAPBOX_STYLES } from './constants.ts';
import { drawModes } from './drawModes.ts';

type MapOptions = {
  zoom?: number;
  minZoom?: number;
  styleURL: string;
};

// eslint-disable-next-line import/no-default-export
export default class MapBox {
  protected readonly map: mapboxgl.Map;
  protected readonly draw: MapboxDraw;

  private resizeObserver: ResizeObserver | null = null;
  private resizeMap!: () => void;

  protected center: mapboxgl.LngLatLike;

  protected selectedPropertyMarker: Marker | undefined;

  constructor(
    container: HTMLElement,
    center: mapboxgl.LngLatLike,
    { minZoom = 4, styleURL, zoom = 13 }: MapOptions,
    init: (instance: MapBox) => void,
  ) {
    this.map = new mapboxgl.Map({
      container,
      style: styleURL,
      center,
      zoom,
      minZoom,
      // eslint-disable-next-line
      accessToken: getConfig('MAPBOX_API_KEY'),
      attributionControl: false,
    });

    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: false,
      },
      boxSelect: false,
      defaultMode: 'simple_select',
      modes: drawModes,
      userProperties: true,
      styles: DRAW_MAPBOX_STYLES,
    });

    this.center = center;
    this.selectedPropertyMarker = undefined;
    this.setupResizeHandling();

    init(this);
  }

  getMap = () => this.map;

  getDraw = () => this.draw;

  setCenter = (center: mapboxgl.LngLatLike) => {
    this.center = center;
    this.map.setCenter(center);
  };

  getCenter = () => this.center;

  addControl = (control: mapboxgl.Control | mapboxgl.IControl) => {
    this.map.addControl(control);
  };

  removeControl = (control: mapboxgl.Control | mapboxgl.IControl) => {
    this.map.removeControl(control);
  };

  setIsPolygonDrawButtonDisabled = (disabled: boolean) => {
    const doc = this.map.getContainer().ownerDocument || document;
    const drawPolygonArray = doc.getElementsByClassName('mapbox-gl-draw_polygon');
    if (drawPolygonArray.length) {
      const drawPolygon = drawPolygonArray[0] as HTMLButtonElement;
      drawPolygon.disabled = disabled;
    }
  };

  disablePolygonDrawButton = () => {
    this.setIsPolygonDrawButtonDisabled(true);
  };

  enablePolygonDrawButton = () => {
    this.setIsPolygonDrawButtonDisabled(false);
  };

  setIsTrashButtonDisabled = (isDisabled: boolean) => {
    const doc = this.map.getContainer().ownerDocument || document;
    const deleteButtonArray = doc.getElementsByClassName(
      'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_trash',
    );

    if (deleteButtonArray.length) {
      const deleteButton = deleteButtonArray[0] as HTMLButtonElement;
      deleteButton.disabled = isDisabled;
    }
  };

  disableTrashButton = () => {
    this.setIsTrashButtonDisabled(true);
  };

  enableTrashButton = () => {
    this.setIsTrashButtonDisabled(false);
  };

  addEventToMap = (
    event: keyof mapboxgl.MapEventType,
    listener: (ev: (mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) & mapboxgl.EventData) => void,
  ) => {
    this.map.on(event, listener);
  };

  deleteAllPolygons = () => this.draw.deleteAll();

  addMarker = (marker: mapboxgl.Marker) => {
    marker.addTo(this.map);
  };

  moveMarker = (lat: number, long: number) => {
    if (this.selectedPropertyMarker) {
      this.selectedPropertyMarker.setLngLat([long, lat]);
    } else {
      throw new Error('No selected property marker');
    }
  };

  getGeoCoords = (): GeoJSON.Position[][] => {
    const drawnPoly = this.draw.getAll().features[0];
    return drawnPoly?.geometry.type === 'Polygon' ? drawnPoly.geometry.coordinates : [[]];
  };

  addPolygon = (polygon: GeoJSON.Position[][]) => {
    this.draw.add({
      type: 'Polygon',
      coordinates: polygon,
    });
  };

  resize = () => {
    this.getMap().resize();
  };

  private setupResizeHandling = () => {
    this.resizeMap = throttle(() => this.getMap().resize(), 550);
    if (this.map.getContainer()) {
      this.resizeObserver = new ResizeObserver(this.resizeMap);
      this.resizeObserver.observe(this.map.getContainer());
    }
    document.addEventListener('fullscreenchange', this.resizeMap);
  };

  destroy = () => {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    document.removeEventListener('fullscreenchange', this.resizeMap);
    this.map.remove();
  };
}
